import React from "react";
import parse from "html-react-parser";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import isValid from "date-fns/isValid";
import ContractJourneyLeg from "./ContractJourneyLeg";
import { isFloatingFleet } from "../../utilities/aircraft";
import { formatLegDepartureDate } from "../../utilities/date";
import { getTripTypeLabelFromLegCount, normalizeTripTypeDisplay } from "../../utilities/tripTypeDisplay";

const formatContractSignatureDate = (value) => {
    if (!value) return false;

    try {
        const parsed = parseISO(value);
        if (isValid(parsed)) {
            return format(parsed, "dd/MM/yy", { awareOfUnicodeTokens: true });
        }
    } catch {
        // Fall through to legacy date formats
    }

    return formatLegDepartureDate(value) || value;
};

const Contract = ({ mode, isReportView, contractData, hideTerms }) => {
    const checkExists = (definition) => {
        if (definition) {
            return definition;
        } else {
            return false;
        }
    };

    const parseAirportDisplay = (str) => {
        if (!str || typeof str !== "string") return { name: "", code: null };
        const dashIndex = str.indexOf(" - ");
        if (dashIndex > -1) {
            const code = str.slice(0, dashIndex).trim();
            const name = str.slice(dashIndex + 3).trim();
            return { name: name || str, code: code || null };
        }
        return { name: str.trim(), code: null };
    };

    // const formatAirportDefinition = (airportStr) => {
    //     const { name, code } = parseAirportDisplay(airportStr)
    //     if (!name) return false
    //     return code ? `${name}<br/><span class="dd--light">(${code})</span>` : name
    // }

    const getPassengerCount = (str) => {
        if (!str || typeof str !== "string") return "-";
        const match = str.match(/(\d+)\s*passenger/i);
        return match ? match[1] : str;
    };

    const getAdditionalTerms = (subjectToConditions, ownerApproval, subjectToWeight) => {
        const terms = [];

        if (subjectToConditions && subjectToConditions !== "None") {
            terms.push(subjectToConditions);
        }

        if (ownerApproval === "Y") {
            terms.push(" Subject to owners approval");
        }

        if (subjectToWeight === "Y") {
            terms.push(" Subject to weight");
        }

        if (terms.length > 0) {
            return terms.join(", ");
        } else {
            return "n/a";
        }
    };

    const DataList = ({ data, className }) => {
        if (data) {
            return (
                <dl className={`contract__data-list ${className}`}>
                    {data.map((item, i) => {
                        return (
                            <React.Fragment key={item.term}>
                                {item.definition && (
                                    <>
                                        <dt>{item.term}</dt>
                                        <dd className={item.className}>
                                            {parse(item.definition)}
                                            {item.smallPrint && (
                                                <>{parse(`<div class="dd--light">* ${item.smallPrint}</div>`)}</>
                                            )}
                                        </dd>
                                    </>
                                )}
                            </React.Fragment>
                        );
                    })}
                </dl>
            );
        }
    };

    const getContractDetails = (contractData) => {
        const terms = [
            {
                term: "Print name:",
                definition: checkExists(contractData.cClientName),
            },
        ];

        if (mode === "view") {
            terms.push({
                term: "Signature:",
                definition: checkExists(contractData.cClientName),
                className: "contract__signature-name",
            });
            terms.push({
                term: "Date:",
                definition: checkExists(
                    formatContractSignatureDate(contractData.signedDateTime || contractData.contractDate)
                ),
            });
        } else if (mode === "sign" && isReportView) {
            terms.push({
                term: "signature:",
                definition: "&nbsp;",
            });
            terms.push({
                term: "Date:",
                definition: "&nbsp;",
            });
        }

        return terms;
    };

    return (
        <div className="contract">
            <section className="contract__section">
                <h2 className="contract__title">Aircraft Charter Agreement</h2>
                <div className="contract__grid">
                    <DataList
                        data={[
                            {
                                term: "Date",
                                definition: checkExists(
                                    contractData.contractDate && formatLegDepartureDate(contractData.contractDate)
                                ),
                            },
                            {
                                term: "Client",
                                definition: checkExists(contractData.cClientName),
                            },
                            {
                                term: "Shy Reference",
                                definition: checkExists(contractData.cShyReference),
                            },
                        ]}
                    />
                    <div className="contract__text">
                        <address>{checkExists(contractData.uShyAddress)}</address>
                    </div>
                </div>
            </section>
            <section className="contract__section contract__section--signature">
                <h2 className="contract__title">Flight information</h2>
                <DataList
                    className="contract__data-list--stack-mobile"
                    data={[
                        {
                            term: "Aircraft",
                            definition: `${checkExists(contractData.cAircraft)}${isFloatingFleet(contractData) ? " (Floating Fleet)" : ""}`,
                        },
                        {
                            term: "Aircraft Category",
                            definition: checkExists(contractData.cAircraftCategory),
                        },
                        ...(contractData.schedule?.length
                            ? [
                                  {
                                      term: "Trip type",
                                      definition:
                                          normalizeTripTypeDisplay(contractData.cTripType) ||
                                          getTripTypeLabelFromLegCount(contractData.schedule.length),
                                  },
                              ]
                            : []),
                        {
                            term: "Baggage",
                            definition: checkExists(contractData.cLuggage),
                            smallPrint: checkExists(contractData.uLuggageSmallPrint),
                        },
                        {
                            term: "Catering",
                            definition: checkExists(contractData.cateringOptionText),
                            smallPrint: checkExists(contractData.uCateringSmallPrint),
                        },
                        {
                            term: "Additional Terms",
                            className: "dd--light dd--uppercase",
                            definition: getAdditionalTerms(
                                contractData.subjectToConditions,
                                contractData.ownerApproval,
                                contractData.subjectToWeight
                            ),
                        },
                        {
                            term: "Special Remarks",
                            className: "dd--light dd--uppercase",
                            definition: checkExists(contractData.uRemarks),
                        },
                    ]}
                />
            </section>

            <section className="contract__section">
                <h2 className="contract__title">Schedule Information</h2>
                {contractData.schedule && contractData.schedule.length > 0 && (
                    <div className="contract__schedule-table">
                        <ContractJourneyLeg
                            legs={contractData.schedule.map((journey) => {
                                const from = parseAirportDisplay(journey.cDepartureAirport);
                                const to = parseAirportDisplay(journey.cArrivalAirport);
                                return {
                                    fromLong: from.name || "",
                                    fromShort: from.code,
                                    toLong: to.name || "",
                                    toShort: to.code,
                                    departureDate: journey.cDepartureDate,
                                    departureTime: journey.cDepartureTime || "",
                                    arrivalDate: journey.cArrivalDate,
                                    arrivalTime: journey.cArrivalTime || "",
                                    duration: journey.cDuration || "",
                                    fuelStop: 0,
                                    passengers: getPassengerCount(contractData.zPassengersAndPets),
                                    luggage:
                                        journey.cLuggageCount !== undefined && journey.cLuggageCount !== null
                                            ? journey.cLuggageCount
                                            : "",
                                };
                            })}
                        />
                    </div>
                )}
                <div className="contract__text contract__text--small">
                    <p>* All times shown local time.</p>
                    <p>
                        * Please note that this booking is subject to owner's approval, slots, de-icing, permits,
                        overflights, parking, permissions, and continued aircraft and crew availability.
                    </p>
                </div>
            </section>

            <section className="contract__section contract__section--payment-terms">
                <h2 className="contract__title">Payment Terms</h2>
                <DataList
                    className="contract__data-list--stack-mobile"
                    data={[
                        {
                            term: "Price",
                            definition: checkExists(contractData.cPrice),
                        },
                        {
                            term: "Payment Terms",
                            className: "dd--uppercase",
                            definition: checkExists(contractData.uTerms),
                        },
                    ]}
                />

                {contractData.uCancellationTerms && (
                    <dl className="contract__data-list contract__data-list--stack-mobile">
                        <dt>Cancellation Terms</dt>
                        <dd>
                            {contractData.uCancellationTerms.map((term, index) => {
                                return <p key={`cancellation-term${index}`}>{term.uTerm}</p>;
                            })}
                        </dd>
                    </dl>
                )}

                <div className="contract__text contract__text--small">
                    <p>* {checkExists(contractData.deIceText)}</p>
                </div>
            </section>
            <section className="contract__section">
                <div className="contract__name">
                    <DataList className="contract__data-list--stack-mobile" data={getContractDetails(contractData)} />
                </div>
            </section>
            {!hideTerms && contractData.termsConditions && (
                <section className="contract__section contract__section--flex-desktop contract__section--terms">
                    <h2 className="contract__title">Terms & Conditions</h2>
                    <div className="contract__terms">
                        {contractData.termsConditions.map((section, index) => {
                            return (
                                <div className="contract__terms__term" key={`term${index}`}>
                                    <h3>
                                        {section.order} {section.sectionTitle}
                                    </h3>
                                    <div className="contract__terms__term__number">{parse(section.terms)}</div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
};

export default Contract;
